import { useState, useCallback } from 'react';
import { useGetPerson } from 'talent_matching/hooks/use_people';
import type { Person, Row } from 'talent_matching/types';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import compact from 'lodash/compact';

export type SelectionsHelper = {
  // State
  selectedPeople: Person[];
  totalSelected: number;
  totalCount: number;
  noneSelected: boolean;
  fromStageName: string | null | undefined;
  toStageName: string | null | undefined;
  toStageId: number | null | undefined;
  applicationIds: number[];
  // Functions
  setPeopleFromSelections: (selectedRows: GridRowSelectionModel | null) => void;
  removeSelections: () => void;
  sameHiringPlan: () => boolean;
  isRowSelected: (id: string | number) => boolean;
};

const useSelectionsHelper = (
  selectablePeople: Row[] = [],
  totalCount: number
): SelectionsHelper => {
  const [selectedPeople, setSelectedPeople] = useState<Person[]>([]);
  const getPerson = useGetPerson();

  const setPeopleFromSelections = useCallback(
    (selectedRows: GridRowSelectionModel | null) => {
      const newSelected = selectedRows?.map((rowId) => {
        const numericId = Number(rowId);
        const cachedPerson = getPerson(numericId);
        return (
          cachedPerson ||
          (selectablePeople.find((p) => p.id === numericId) as Person)
        );
      });

      setSelectedPeople(newSelected || []);
    },
    [getPerson, selectablePeople]
  );

  const removeSelections = useCallback(() => {
    setSelectedPeople([]);
  }, [setSelectedPeople]);

  const totalSelected = selectedPeople.length;
  const noneSelected = totalSelected === 0;

  const sameHiringPlan = useCallback(() => {
    if (noneSelected) {
      return false;
    }

    const hiringPlanId = selectedPeople[0]?.hiringPlanId;
    if (!hiringPlanId) {
      return false;
    }

    return selectedPeople.every(
      (person) => person?.hiringPlanId === hiringPlanId
    );
  }, [selectedPeople, noneSelected]);

  const fromStageName =
    selectedPeople[0]?.currentHiringPlanInterviewStage?.name;
  const toStageName = selectedPeople[0]?.nextHiringPlanInterviewStage?.name;
  const toStageId = selectedPeople[0]?.nextHiringPlanInterviewStage?.id;

  const applicationIds = compact(selectedPeople.map((person) => person.id));
  const isRowSelected = (id: string | number) => {
    return selectedPeople.some(
      (person) => person.id.toString() === id.toString()
    );
  };

  return {
    // State
    selectedPeople,
    totalSelected,
    totalCount,
    noneSelected,
    fromStageName,
    toStageName,
    toStageId,
    applicationIds,
    // Functions
    setPeopleFromSelections,
    removeSelections,
    sameHiringPlan,
    isRowSelected,
  };
};

export default useSelectionsHelper;
